import { faRotateRight, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  B2Button,
  B2Card,
  B2Form,
  B2Input,
  B2RadioButton,
  B2Switch,
} from "@hdb/biome-2";
import { ErrorMessage, Formik } from "formik";
import { FC, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import HasPermission from "../../../../../../core/guards/has-permission";
import isModuleAndActionAvailable from "../../../../../../core/guards/is-module-and-action-available";
import SkeletonComponent from "../../../../../../shared/custom-spinner/skeleton.component";
import { safeAtob } from "../../../../../../shared/helpers/enode-decode-string";
import "./asset-action-form.scss";

export interface Control {
  name: string;
  label: string;
  placeholder?: string;
  type:
    | string
    | "text"
    | "textarea"
    | "number"
    | "checkbox"
    | "switch"
    | "date"
    | "datetime"
    | "email"
    | "select"
    | "chips"
    | "radio"
    | "password";
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  suffix?: string;
  options?: { value: any; label: any }[];
  selectableValue?: string;
  display?: boolean;
  dispatchableApi?: any;
  onChange?: (selectedValue: any) => void;
  gridFullWidth?: boolean;
}

export interface Controls {
  label: string;
  isGrid: boolean;
  gridClass?: string;
  controlGroup: Control[];
}

export interface AssetActioFormConfig {
  controls: Controls[];
}

interface AssetActioFormComponentProps {
  formRef: any;
  formConfig: AssetActioFormConfig;
  initialValues: any;
  actionConfig?: any;
  refreshActionConfig?: any;
  isLoadingData?: boolean;
  validation?: any;
  moduleName?: any;
  actionName?: any;
}

const AssetActionFormComponent: FC<AssetActioFormComponentProps> = ({
  formRef,
  formConfig,
  initialValues,
  actionConfig,
  refreshActionConfig,
  isLoadingData = false,
  validation = {},
  moduleName,
  actionName,
}) => {
  const params = useParams();
  const id = params.id ? safeAtob(params.id) : null;
  const dispatch = useDispatch();
  const { controls } = formConfig;
  const [chipsValues, setChipsValues] = useState([] as any);
  const [chipsCurrentValue, setChipsCurrentValue] = useState("");
  const [chipsList, setChipsListe] = useState([] as any);
  const [showChipsList, setShowChipsList] = useState(false);
  const [disabledStatus, setDisabledStatus] = useState(false);
  const [renderCount, setRenderCount] = useState(0);

  const onChangeChipsItem = (
    setFieldValue: any,
    name: any,
    currentValue: any
  ) => {
    if (!chipsList.find((item: any) => item.assetCode === currentValue)) return;
    let copyChipsValues = [...chipsValues];

    if (copyChipsValues.find((chip: any) => chip === currentValue)) {
      copyChipsValues = copyChipsValues.filter(
        (chip: any) => chip !== currentValue
      );
      setFieldValue(name, copyChipsValues);
      setChipsValues(copyChipsValues);
      return;
    }

    if (copyChipsValues.length >= 3) return;

    if (!copyChipsValues.find((chip: any) => chip === currentValue)) {
      copyChipsValues.push(currentValue);
      setFieldValue(name, copyChipsValues);
      setChipsValues(copyChipsValues);
    }
  };
  const onRemoveChipsItem = (index: any, setFieldValue: any, name: any) => {
    let copyChipsValues = [...chipsValues];
    copyChipsValues.splice(index, 1);
    setFieldValue(name, copyChipsValues);
    setChipsValues(copyChipsValues);
  };

  const getOptionList = (api: any, value: any) => {
    if (value.length % 3 === 0) {
      dispatch(api({ searchText: value, gatewayCode: id })).then((r: any) => {
        setChipsListe(r.payload.data);
        setShowChipsList(true);
      });
    }
  };

  const searchBoxRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: any) => {
    if (
      searchBoxRef.current &&
      !searchBoxRef.current.contains(event.target as Node)
    ) {
      setShowChipsList(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(initialValues).length && Object.keys(formConfig).length) {
      // for chips

      const chipsControl: any = formConfig.controls[0].controlGroup.find(
        (control: any) => control.type === "chips"
      );
      if (chipsControl) {
        const values = initialValues[chipsControl.name];
        if (values) {
          const jsonValue = values;
          const copyChipsValues = [...chipsValues];
          for (let item of jsonValue) {
            if (!copyChipsValues.find((chip: any) => chip === item)) {
              copyChipsValues.push(item);
              setChipsValues(copyChipsValues);
            }
          }
        }
      }
    }
  }, [initialValues, formConfig]);

  const modules = useSelector((state: any) => state.navbar.modules);

  const getDisabledStatus = async () => {
    if (moduleName && actionName) {
      const isAvailable = await isModuleAndActionAvailable(
        moduleName,
        actionName,
        modules
      );

      setDisabledStatus(!isAvailable);
    }
  };

  useEffect(() => {
    getDisabledStatus();
  }, []);

  return (
    <div className="flex flex-col items-start">
      <SkeletonComponent
        wrapperHeight="260px"
        count={3}
        skeletonHeight="80px"
        isLoading={isLoadingData ?? false}
        isShowCardStyle={true}
        width="100%"
      >
        {Object.keys(initialValues).length ? (
          <div className="w-100">
            <Formik
              innerRef={formRef}
              enableReinitialize
              onSubmit={() => {}}
              initialValues={initialValues}
              validationSchema={validation}
            >
              {({
                handleSubmit,
                handleChange,
                setFieldValue,
                values,
                touched,
                errors,
              }) => (
                <B2Form noValidate onSubmit={handleSubmit}>
                  <div className="">
                    {controls?.map((c, i: number) => (
                          <div key={i + 0}>
                            {refreshActionConfig?.map((action: any) => (
                                <B2Card.Header key={action?.label || action?.event?.name} className="fs-5 fw-bold header-bg">
                                  <div className="d-flex form-header dashboard-generic-actions align-items-center">
                                    <p className="m-0 flex-grow-1">{c.label}</p>
                                    <button
                                      className="ms-2 cursor-pointer fs-6"
                                      type="button"
                                      style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                      }}
                                      onClick={action?.event}
                                    >
                                      <FontAwesomeIcon
                                        icon={faRotateRight}
                                        className="me-2"
                                      />
                                      Refresh
                                    </button>
                                  </div>
                                </B2Card.Header>
                              ))}
                            <B2Card.Body>
                              <div
                                className={`${
                                  c.isGrid && !c.gridClass
                                    ? "sec-filter-grid"
                                    : c.isGrid && c.gridClass
                                    ? `${c.gridClass} p-0`
                                    : "flex flex-col"
                                } form-content`}
                                style={!c.isGrid ? { gap: "1rem" } : {}}
                              >
                                 {c.controlGroup.map((control) => (
                                   <div
                                     key={control?.name}
                                     className={`flex items-center w-100 ${
                                      control.gridFullWidth
                                        ? "full-width-item"
                                        : ""
                                    }`}
                                  >
                                    {(() => {
                                      switch (control.type) {
                                        case "text":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Input
                                                    type="text"
                                                    placeholder={
                                                      control.placeholder
                                                    }
                                                    name={control.name}
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "email":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Input
                                                    type="email"
                                                    placeholder={
                                                      control.placeholder
                                                    }
                                                    name={control.name}
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "password":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Input
                                                    type="password"
                                                    placeholder={
                                                      control.placeholder
                                                    }
                                                    name={control.name}
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "number":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>

                                              <div className="d-flex align-items-center w-100">
                                                <div className="w-100 mb-2">
                                                  <B2Form.Group controlId="validationFormik01">
                                                    <B2Input
                                                      type="number"
                                                      min={0}
                                                      placeholder={
                                                        control.placeholder
                                                      }
                                                      name={control.name}
                                                      onChange={(e: any) => {
                                                        handleChange(e);
                                                        if (
                                                          Number(
                                                            e.target.value
                                                          ) < 0
                                                        ) {
                                                          e.target.value = 0;
                                                          setFieldValue(
                                                            control?.name,
                                                            0
                                                          );
                                                          return;
                                                        }
                                                        setFieldValue(
                                                          control?.name,
                                                          e.target.value
                                                        );
                                                      }}
                                                      value={
                                                        values[control?.name]
                                                      }
                                                      required={
                                                        control.required ??
                                                        false
                                                      }
                                                      readOnly={
                                                        control.readOnly ??
                                                        false
                                                      }
                                                      disabled={ Boolean(
                                                        control.disabled ||
                                                        disabledStatus
                                                      )}
                                                      isValid={
                                                        touched[control.name] &&
                                                        !errors[control.name]
                                                      }
                                                      isInvalid={
                                                        (!!touched[
                                                          control.name
                                                        ] &&
                                                          !!errors[
                                                            control.name
                                                          ]) ??
                                                        false
                                                      }
                                                    />
                                                    <ErrorMessage
                                                      name={control.name}
                                                      component="div"
                                                      className="error-message"
                                                    />
                                                  </B2Form.Group>
                                                </div>

                                                {control.suffix && (
                                                  <div
                                                    className="mb-2"
                                                    style={{
                                                      width: "50px",
                                                      marginLeft: "1rem",
                                                    }}
                                                  >
                                                    {control.suffix}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        case "textarea":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Input
                                                    as="textarea"
                                                    rows={3}
                                                    placeholder={
                                                      control.placeholder
                                                    }
                                                    name={control.name}
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "date":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Input
                                                    type="date"
                                                    placeholder={
                                                      control.placeholder
                                                    }
                                                    name={control.name}
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "datetime":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Input
                                                    type="datetime-local"
                                                    placeholder={
                                                      control.placeholder
                                                    }
                                                    name={control.name}
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "time":
                                          return (
                                            <div className="flex flex-col items-start w-full">
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Input
                                                    type="time"
                                                    value={values[control.name]}
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    name={control.name}
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "switch":
                                          return (
                                            <div className="d-flex items-center gap-3 justify-start w-full mb-3">
                                              <div>
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2Switch
                                                    type="switch"
                                                    name={control.name}
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.checked
                                                      );
                                                    }}
                                                    checked={
                                                      values[control?.name]
                                                    }
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      !!(
                                                        touched[control.name] &&
                                                        errors[control.name]
                                                      )
                                                    }
                                                  />

                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                              <div style={{ width: "55%" }}>
                                                <div
                                                  style={{ display: "flex" }}
                                                >
                                                  {control.label}

                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        case "radio":
                                          return (
                                            <div className="d-flex items-center justify-center w-full">
                                              <div
                                                className=""
                                                style={{ width: "55%" }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}

                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                <B2Form.Group controlId="validationFormik01">
                                                  <B2RadioButton
                                                    // type="radio"
                                                    id={control.name}
                                                    name={control.name}
                                                    label={control.label}
                                                    disabled={ Boolean(
                                                      control.disabled ||
                                                      disabledStatus
                                                    )}
                                                    readOnly={
                                                      control.readOnly ?? false
                                                    }
                                                    onChange={(e: any) => {
                                                      handleChange(e);
                                                      setFieldValue(
                                                        control?.name,
                                                        e.target.value
                                                      );
                                                    }}
                                                    checked={
                                                      values[control?.name]
                                                    }
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                  />

                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </B2Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "select":
                                          return (
                                            <div
                                              key={renderCount}
                                              className="flex flex-col items-start justify-between  align-items-center w-full"
                                            >
                                              <div className="w-100 mb-2">
                                                <div
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  {control.label}
                                                  {control.required && (
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-100 mb-2">
                                                {/* <B2Form.Group controlId="validationFormik01">
                                                  <B2Select
                                                    name={control.name}
                                                    onChange={(
                                                      selectedOption: any,
                                                      event: any
                                                    ) => {
                                                      if (
                                                        control.onChange &&
                                                        selectedOption
                                                      ) {
                                                        let val: any = 0;

                                                        if (
                                                          isNaN(
                                                            Number(
                                                              selectedOption?.value
                                                            )
                                                          )
                                                        ) {
                                                          val =
                                                            selectedOption?.value;
                                                        } else {
                                                          val = Number(
                                                            selectedOption?.value
                                                          );
                                                        }
                                                        control.onChange(val);
                                                      }
                                                      setFieldValue(
                                                        control?.name,
                                                        selectedOption?.value
                                                      );
                                                    }}
                                                    value={
                                                      values[control?.name]
                                                    }
                                                    required={
                                                      control.required ?? false
                                                    }
                                                    defaultValue={
                                                      values[control?.name]
                                                    }
                                                    disabled={
                                                      control.disabled ?? false
                                                    }
                                                    isValid={
                                                      touched[control.name] &&
                                                      !errors[control.name]
                                                    }
                                                    isInvalid={
                                                      (!!touched[
                                                        control.name
                                                      ] &&
                                                        !!errors[
                                                          control.name
                                                        ]) ??
                                                      false
                                                    }
                                                    optionList={
                                                      control.options || []
                                                    }
                                                  />

                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                
                                                </B2Form.Group> */}

                                                <Form.Group
                                                  controlId={`validation + ${control.name}`}
                                                >
                                                  <div className="d-flex align-items-center">
                                                    <Form.Select
                                                      name={control.name}
                                                      onChange={(event) => {
                                                        handleChange(event);
                                                        if (control.onChange) {
                                                          control.onChange(
                                                            event.target.value
                                                          );
                                                        }
                                                      }}
                                                      value={
                                                        values[control?.name]
                                                      }
                                                      required={
                                                        control.required ??
                                                        false
                                                      }
                                                      disabled={ Boolean(
                                                        control.disabled ||
                                                        disabledStatus
                                                      )}
                                                      isValid={
                                                        touched[control.name] &&
                                                        !errors[control.name]
                                                      }
                                                      isInvalid={
                                                        (!!touched[
                                                          control.name
                                                        ] &&
                                                          !!errors[
                                                            control.name
                                                          ]) ??
                                                        false
                                                      }
                                                    >
                                                      <option value={""}>
                                                        {control.placeholder}
                                                      </option>
                                                      {control.options?.map(
                                                        (
                                                          option: any,
                                                          index: any
                                                        ) => (
                                                          <option
                                                            key={index}
                                                            value={option.value}
                                                          >
                                                            {option.label}
                                                          </option>
                                                        )
                                                      )}
                                                    </Form.Select>
                                                    {/* {control?.isLoading && (
                                                      <Spinner
                                                        animation="border"
                                                        variant="primary"
                                                        className="ms-1"
                                                        size="sm"
                                                      />
                                                    )} */}
                                                  </div>
                                                  <ErrorMessage
                                                    name={control.name}
                                                    component="div"
                                                    className="error-message"
                                                  />
                                                </Form.Group>
                                              </div>
                                            </div>
                                          );
                                        case "chips":
                                          return (
                                            <div
                                              className="w-100 mb-2"
                                              style={{ position: "relative" }}
                                            >
                                              <div
                                                className={`w-100 mb-2 flex flex-col items-start  ${
                                                  control.hasOwnProperty(
                                                    "display"
                                                  ) && !control.display
                                                    ? "d-none"
                                                    : ""
                                                }`}
                                              >
                                                <div className="w-100 mb-2">
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    {control.label}
                                                    {control.required && (
                                                      <span className="text-danger">
                                                        *
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="w-100">
                                                  <B2Form.Group controlId="validationFormik01">
                                                    <B2Input
                                                      type="text"
                                                      placeholder={
                                                        control.placeholder
                                                      }
                                                      name="chipsControl"
                                                      onClick={() =>
                                                        setShowChipsList(true)
                                                      }
                                                      onChange={(
                                                        event: any
                                                      ) => {
                                                        setChipsCurrentValue(
                                                          event.target.value
                                                        );
                                                        getOptionList(
                                                          control.dispatchableApi,
                                                          event.target.value
                                                        );
                                                      }}
                                                      onKeyUp={(event: any) => {
                                                        if (
                                                          event.key === "Enter"
                                                        ) {
                                                          setChipsCurrentValue(
                                                            ""
                                                          );
                                                          onChangeChipsItem(
                                                            setFieldValue,
                                                            control.name,
                                                            chipsCurrentValue
                                                          );
                                                        }
                                                      }}
                                                      value={chipsCurrentValue}
                                                      required={
                                                        control.required ??
                                                        false
                                                      }
                                                      readOnly={
                                                        control.readOnly ??
                                                        false
                                                      }
                                                      disabled={ Boolean(
                                                        control.disabled ||
                                                        disabledStatus
                                                      )}
                                                      isValid={
                                                        touched[control.name] &&
                                                        !errors[control.name]
                                                      }
                                                      isInvalid={
                                                        (!!touched[
                                                          control.name
                                                        ] &&
                                                          !!errors[
                                                            control.name
                                                          ]) ??
                                                        false
                                                      }
                                                    />
                                                    <ErrorMessage
                                                      name={control.name}
                                                      component="div"
                                                      className="error-message"
                                                    />
                                                  </B2Form.Group>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      gap: "1rem",
                                                      flexWrap: "wrap",
                                                      marginTop: "1rem",
                                                    }}
                                                  >
                                                    {chipsValues.map(
                                                      (
                                                        chip: any,
                                                        index: any
                                                      ) => (
                                                         <div
                                                           key={index}
                                                           style={{
                                                            backgroundColor:
                                                              "#64c8c8",
                                                            display: "flex",
                                                            justifyContent:
                                                              "space-between",
                                                            padding:
                                                              ".3rem .7rem",
                                                            borderRadius:
                                                              "10px",
                                                          }}
                                                        >
                                                          {chip}
                                                          <button
                                                            type="button"
                                                            style={{
                                                              cursor: "pointer",
                                                              padding: 0,
                                                              border: "none",
                                                              backgroundColor: "transparent",
                                                            }}
                                                            onClick={(e) => {
                                                              onRemoveChipsItem(
                                                                index,
                                                                setFieldValue,
                                                                control.name
                                                              );
                                                            }}
                                                          >
                                                            <FontAwesomeIcon
                                                              icon={faTimes}
                                                              className="fs-7 ms-2"
                                                            />
                                                          </button>
                                                        </div>
                                                      )
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              {chipsList?.length &&
                                              showChipsList ? (
                                                <div
                                                  ref={searchBoxRef}
                                                  className="bg-card shadow-sm"
                                                  style={{
                                                    position: "absolute",
                                                    top: "70px",
                                                    padding: "1rem",
                                                    maxHeight: "300px",
                                                    overflowY: "auto",
                                                  }}
                                                >
                                                  {chipsList?.map(
                                                    (chip: any) => (
                                                      <button
                                                        key={chip.assetCode}
                                                        type="button"
                                                        onClick={() =>
                                                          onChangeChipsItem(
                                                            setFieldValue,
                                                            control.name,
                                                            chip.assetCode
                                                          )
                                                        }
                                                        style={{
                                                          padding:
                                                            "0.5rem 1rem",
                                                          cursor: "pointer",
                                                          color:
                                                            chipsValues.includes(
                                                              chip.assetCode
                                                            )
                                                              ? "#64c8c8"
                                                              : "",
                                                          background: "none",
                                                          border: "none",
                                                        }}
                                                      >
                                                        {chip.assetCode}
                                                      </button>
                                                    )
                                                  )}
                                                </div>
                                              ) : null}
                                            </div>
                                          );

                                        default:
                                          return null;
                                      }
                                    })()}
                                    {/* <div
                                      style={{
                                        width: "50px",
                                        marginLeft: "1rem",
                                      }}
                                    >
                                      control.suffix
                                    </div> */}
                                  </div>
                                ))}
                              </div>

                              {/* actions */}
                              {actionConfig?.length && (
                                <div className="mt-3">
                                  {actionConfig?.map((action: any, idx: number) =>
                                      action?.moduleName ? (
                                        <HasPermission
                                          key={action?.label || idx}
                                          moduleName={action?.moduleName}
                                          actionName={action?.actionName}
                                          isRoute={false}
                                        >
                                          <B2Button
                                            onClick={action?.event}
                                            variant={action?.variant}
                                          >
                                            {action?.label}
                                          </B2Button>
                                        </HasPermission>
                                      ) : (
                                        <B2Button
                                          key={action?.label || idx}
                                          onClick={action?.event}
                                          variant={action?.variant}
                                        >
                                          {action?.label}
                                        </B2Button>
                                      )
                                    )}
                                </div>
                              )}
                            </B2Card.Body>
                          </div>
                        ))
                    }
                  </div>
                </B2Form>
              )}
            </Formik>
          </div>
        ) : null}
      </SkeletonComponent>
    </div>
  );
};

export default AssetActionFormComponent;
